<template>
  <div>
    <SoAlert
      v-if="isCardPaymentNotFound"
      type="error"
      data-testid="card-payment-summary-not-found-error"
      class="my-5"
    >
      The payment has failed. Please close this window and try again.
    </SoAlert>

    <SoAlert
      v-else-if="paymentDetails.isError"
      type="error"
      data-testid="card-payment-summary-error"
      class="my-5"
    >
      Failed to fetch payment details.
    </SoAlert>

    <SoAlert
      v-else-if="isPollingTimedOut"
      type="warning"
      data-testid="card-payment-summary-pending-alert"
      class="my-5"
    >
      The payment was submitted, but its confirmation is taking longer than
      expected. Please check the Transactions tab in a few minutes before trying
      again.
    </SoAlert>

    <template v-else-if="paymentDetails.data">
      <SoSummaryTable
        v-if="paymentDetails.data"
        title="Payment summary"
        data-testid="card-payment-summary-table"
        :items="summary"
      />

      <SoAlert
        type="success"
        data-testid="card-payment-summary-success-alert"
        class="mt-5"
      >
        The payment has been processed. Payments may take up to a day to appear
        on the customer account
      </SoAlert>
    </template>

    <div v-else data-testid="card-payment-summary-skeleton">
      <SoSkeleton width="12rem" height="1.5rem" class="mt-3 mb-6" />
      <SoSkeleton v-for="i in 4" :key="i" height="1.75rem" class="mb-4" />
      <SoSkeleton height="5rem" class="mt-5" />
    </div>
  </div>
</template>

<script lang="ts">
  import SoSummaryTable from "@/components/SoSummaryTable.vue"
  import {
    SoSummaryTableItem,
    SoSummaryTableValueType,
  } from "@/types/components/SoSummaryTable"
  import SoAlert from "@/components/SoAlert.vue"
  import { defineComponent, PropType } from "vue"
  import { CardPaymentService } from "@/services/CardPaymentService"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import {
    CardPaymentStatus,
    PaymentNotificationStatus,
    JuniferPaymentStatus,
  } from "@/models/cardPaymentDetails/cardPaymentDetails.types"

  type CardPaymentFormData = {
    cardHolderName?: string
    contactEmail: string
    amountInPounds: string
  }

  // The capture webhook usually lands within seconds; give it 30s before
  // telling the agent to check the Transactions tab instead.
  const POLL_INTERVAL_MS = 2000
  const MAX_POLL_ATTEMPTS = 15

  export default defineComponent({
    components: {
      SoSummaryTable,
      SoAlert,
      SoSkeleton,
    },
    props: {
      paymentId: {
        type: String,
        required: true,
      },
      formData: {
        type: Object as PropType<CardPaymentFormData>,
        required: true,
      },
      /**
       * checkout.com Flow payments (SO-30708) are confirmed asynchronously by
       * webhook, so the payment row may not exist yet when this component
       * mounts. When set, the row being missing means "not confirmed yet"
       * (poll until it appears) rather than "payment failed".
       */
      pollUntilFound: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        CardPaymentStatus,
        JuniferPaymentStatus,
        PaymentNotificationStatus,
        paymentDetails: this.pollUntilFound
          ? CardPaymentService.findCardPaymentDetails(this.paymentId)
          : CardPaymentService.getCardPaymentDetails(this.paymentId),
        pollAttempts: 0,
        pollTimer: undefined as ReturnType<typeof setTimeout> | undefined,
      }
    },

    computed: {
      isCardPaymentNotFound() {
        return !!this.paymentDetails.error?.message.includes(
          "Card payment not found",
        )
      },
      isPollingTimedOut() {
        return (
          this.pollUntilFound &&
          !this.paymentDetails.data &&
          this.pollAttempts >= MAX_POLL_ATTEMPTS
        )
      },
      summary(): SoSummaryTableItem[] {
        const payment = this.paymentDetails.data
        if (!payment) return []
        const cardHolderRow: SoSummaryTableItem[] = this.formData.cardHolderName
          ? [
              {
                title: "Card holder name",
                value: this.formData.cardHolderName,
              },
            ]
          : []
        return [
          ...cardHolderRow,
          {
            title: "Contact email",
            value: this.formData.contactEmail,
          },
          { title: "Reference:", value: payment.reference },
          {
            title: "Amount",
            value: payment.amountInPence / 100,
            type: SoSummaryTableValueType.MONEY,
          },
        ]
      },
    },

    mounted() {
      if (this.pollUntilFound) this.schedulePoll()
    },

    unmounted() {
      clearTimeout(this.pollTimer)
    },

    methods: {
      schedulePoll() {
        if (this.paymentDetails.data || this.pollAttempts >= MAX_POLL_ATTEMPTS)
          return

        this.pollTimer = setTimeout(async () => {
          // the row may have appeared while this poll was pending
          if (this.paymentDetails.data) return
          this.pollAttempts += 1
          await this.paymentDetails.refetch()
          this.schedulePoll()
        }, POLL_INTERVAL_MS)
      },
    },
  })
</script>

<style scoped>
  .payment-provider-logo {
    height: 2rem;
  }
</style>
